import React, { useEffect, useState } from "react";
import { Check, Crown, AlertTriangle, CreditCard } from "lucide-react";

import {
  getSubscription,
  createSubscription,
  verifySubscription,
} from "../api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function SubscriptionView() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
  };

  // silent=true refreshes data without showing the full-page spinner
  const loadSubscription = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getSubscription();
      setSubscription(data);
      return data;
    } catch (err) {
      console.error("Subscription load error:", err);
      showMessage(
        err?.response?.data?.message || "Unable to load subscription.",
        "error"
      );
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscription();
  }, []);

  // After checkout the webhook may arrive a few seconds later.
  // Poll the backend (never the browser) for the real status.
  const waitForActivation = async () => {
    for (let i = 0; i < 8; i++) {
      const latest = await loadSubscription(true);
      if (latest?.status === "ACTIVE") return true;
      await sleep(3000);
    }
    return false;
  };

  const handleCheckoutSuccess = async (response) => {
    try {
      showMessage("Payment completed. Confirming your subscription...", "info");

      try {
        await verifySubscription({
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_subscription_id: response.razorpay_subscription_id,
          razorpay_signature: response.razorpay_signature,
        });
      } catch (e) {
        // Webhook can still activate it. Keep waiting instead of failing.
        console.warn("Verify call failed, waiting for webhook:", e);
      }

      const active = await waitForActivation();

      if (active) {
        showMessage("Your subscription is active. Thank you!", "success");
      } else {
        showMessage(
          "Razorpay has your authorization. Your plan activates as soon as the ₹300 charge is confirmed. Please refresh in a minute. Do not pay again.",
          "info"
        );
      }
    } finally {
      setCreating(false);
    }
  };

  const handleSubscribe = async () => {
    if (creating) return;

    try {
      setCreating(true);
      showMessage("", "");

      const data = await createSubscription();

      // Backend says: nothing to pay right now (retrying, authorized, etc.)
      if (!data?.checkoutRequired) {
        showMessage(data?.message || "No payment action is needed.", "info");
        await loadSubscription(true);
        setCreating(false);
        return;
      }

      if (!data?.subscriptionId) {
        throw new Error("Razorpay subscription ID was not returned by the server.");
      }
      if (!window.Razorpay) {
        throw new Error("Razorpay Checkout is not loaded. Please refresh the page.");
      }

      const key = data.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!key) {
        throw new Error("Razorpay Key ID is not configured.");
      }

      const razorpay = new window.Razorpay({
        key,
        name: "Restaurant POS",
        description: "Restaurant POS Basic - ₹300/month",
        subscription_id: data.subscriptionId,
        theme: { color: "#e4572e" },

        handler: handleCheckoutSuccess,

        modal: {
          ondismiss: function () {
            // Same subscription ID is reused on the next click.
            setCreating(false);
            showMessage(
              "Payment window closed. Press the button again to continue - you will not be charged twice.",
              "info"
            );
          },
        },
      });

      razorpay.on("payment.failed", function (response) {
        console.error("Razorpay payment failed:", response);
        showMessage(
          response?.error?.description ||
            "Payment failed. You can retry in the same window or close it and press the button again.",
          "error"
        );
      });

      razorpay.open();
    } catch (err) {
      console.error("Subscription start error:", err);
      showMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to start subscription.",
        "error"
      );
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="so-sub so-sub-loading-screen min-h-full flex items-center justify-center p-8">
        <style>{SO_SUB_STYLES}</style>
        <div className="text-center">
          <div className="so-sub-spinner mx-auto mb-4"></div>
          <p className="so-sub-loading-text">Loading subscription...</p>
        </div>
      </div>
    );
  }

  const status = subscription?.status || "UNKNOWN";
  const isTrial = status === "TRIALING";
  const isActive = status === "ACTIVE";
  const isPastDue = status === "PAST_DUE";
  const isCancelled = status === "CANCELLED";
  const isExpired = status === "EXPIRED";
  const daysRemaining = Number(subscription?.trialDaysRemaining ?? 0);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";

  const buttonLabel = isActive
    ? "Subscription Active"
    : creating
    ? "Please wait..."
    : isPastDue
    ? "Retry payment"
    : isExpired || isCancelled
    ? "Subscribe again for ₹300/month"
    : "Subscribe for ₹300/month";

  return (
    <div className="so-sub max-w-5xl mx-auto p-4 sm:p-8">
      <style>{SO_SUB_STYLES}</style>

      <div className="text-center mb-10">
        <div className="so-sub-crown-wrap inline-flex items-center justify-center h-14 w-14 rounded-full mb-4">
          <Crown size={28} className="so-sub-crown-icon" />
        </div>
        <h1 className="so-sub-h1">Restaurant POS Subscription</h1>
        <p className="so-sub-subtext mt-2">Simple pricing for your restaurant</p>
      </div>

      <div className="so-sub-panel p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="so-sub-label">Current Plan</p>
            <h2 className="so-sub-plan-name mt-1">
              {subscription?.plan || "TRIAL"}
            </h2>
          </div>

          <div>
            <p className="so-sub-label mb-1">Status</p>
            <span
              className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${
                isActive
                  ? "so-sub-badge-active"
                  : isTrial
                  ? "so-sub-badge-trial"
                  : "so-sub-badge-expired"
              }`}
            >
              {status}
            </span>
          </div>

          <div>
            <p className="so-sub-label">Remaining</p>
            {isTrial ? (
              <p className="so-sub-remaining-neutral">
                {daysRemaining} day{daysRemaining === 1 ? "" : "s"}
              </p>
            ) : isActive ? (
              <p className="so-sub-remaining-active">Active</p>
            ) : isPastDue ? (
              <p className="so-sub-remaining-expired">Payment failed</p>
            ) : isCancelled ? (
              <p className="so-sub-remaining-expired">Cancelled</p>
            ) : (
              <p className="so-sub-remaining-expired">Expired</p>
            )}
          </div>
        </div>

        {isTrial && (
          <div className="so-sub-info-trial mt-6 p-4">
            <div className="flex gap-3">
              <AlertTriangle
                size={22}
                className="so-sub-info-icon-trial flex-shrink-0 mt-0.5"
              />
              <div>
                <h3 className="so-sub-info-title-trial">7-Day Free Trial</h3>
                <p className="so-sub-info-text-trial text-sm mt-1">
                  Your free trial is active. You have{" "}
                  <strong>
                    {daysRemaining} day{daysRemaining === 1 ? "" : "s"}
                  </strong>{" "}
                  remaining.
                </p>
                {subscription?.trialEnd && (
                  <p className="so-sub-info-sub-trial text-xs mt-2">
                    Trial ends on {formatDate(subscription.trialEnd)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {isActive && (
          <div className="so-sub-info-active mt-6 p-4">
            <h3 className="so-sub-info-title-active">Subscription Active</h3>
            <p className="so-sub-info-text-active text-sm mt-1">
              Your Restaurant POS subscription is active and renews
              automatically through UPI AutoPay.
            </p>
            {subscription?.currentPeriodEnd && (
              <p className="so-sub-info-sub-active text-xs mt-2">
                Current billing period ends on{" "}
                {formatDate(subscription.currentPeriodEnd)}
              </p>
            )}
          </div>
        )}

        {isPastDue && (
          <div className="so-sub-info-expired mt-6 p-4">
            <div className="flex gap-3">
              <AlertTriangle
                size={22}
                className="so-sub-info-icon-expired flex-shrink-0"
              />
              <div>
                <h3 className="so-sub-info-title-expired">
                  Last payment failed
                </h3>
                <p className="so-sub-info-text-expired text-sm mt-1">
                  Razorpay may still be retrying your AutoPay. Make sure your
                  UPI account has ₹300 available. If retries are exhausted,
                  press "Retry payment" to set up AutoPay again.
                  {subscription?.currentPeriodEnd &&
                    ` Access continues until ${formatDate(
                      subscription.currentPeriodEnd
                    )}.`}
                </p>
              </div>
            </div>
          </div>
        )}

        {isCancelled && (
          <div className="so-sub-info-expired mt-6 p-4">
            <h3 className="so-sub-info-title-expired">Subscription cancelled</h3>
            <p className="so-sub-info-text-expired text-sm mt-1">
              AutoPay is cancelled and you will not be charged again.
              {subscription?.currentPeriodEnd &&
                ` You can use the POS until ${formatDate(
                  subscription.currentPeriodEnd
                )}.`}
            </p>
          </div>
        )}

        {isExpired && (
          <div className="so-sub-info-expired mt-6 p-4">
            <div className="flex gap-3">
              <AlertTriangle
                size={22}
                className="so-sub-info-icon-expired flex-shrink-0"
              />
              <div>
                <h3 className="so-sub-info-title-expired">
                  Subscription Expired
                </h3>
                <p className="so-sub-info-text-expired text-sm mt-1">
                  Your free trial or previous subscription has expired.
                  Subscribe below to continue using Restaurant POS.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-xl mx-auto">
        <div className="so-sub-plan-card overflow-hidden">
          <div className="so-sub-plan-top p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="so-sub-plan-kicker">RESTAURANT POS</p>
                <h2 className="so-sub-plan-title mt-1">Basic Plan</h2>
              </div>
              <CreditCard size={30} />
            </div>
            <div className="mt-6">
              <span className="so-sub-price">₹300</span>
              <span className="so-sub-price-period"> / month</span>
            </div>
          </div>

          <div className="p-6">
            <h3 className="so-sub-features-title mb-4">Everything included</h3>

            <div className="grid sm:grid-cols-2 gap-3">
              <Feature text="Restaurant POS" />
              <Feature text="Direct POS" />
              <Feature text="Menu Management" />
              <Feature text="Order Management" />
              <Feature text="Kitchen Orders" />
              <Feature text="Restaurant Tables" />
              <Feature text="Order History" />
              <Feature text="Dashboard Analytics" />
              <Feature text="Reports" />
              <Feature text="User Accounts" />
              <Feature text="Chef Accounts" />
              <Feature text="7-Day Free Trial" />
            </div>

            <button
              type="button"
              onClick={handleSubscribe}
              disabled={creating || isActive}
              className={`so-sub-subscribe-btn w-full mt-8 py-3.5 ${
                isActive
                  ? "so-sub-btn-active"
                  : creating
                  ? "so-sub-btn-waiting"
                  : "so-sub-btn-default"
              }`}
            >
              {buttonLabel}
            </button>

            {!isActive && (
              <p className="so-sub-secure-note text-xs text-center mt-3">
                Secure UPI AutoPay through Razorpay. Authorize once, renews monthly.
              </p>
            )}
          </div>
        </div>
      </div>

      {message && (
        <div
          className={`max-w-xl mx-auto mt-6 rounded-xl border p-4 text-sm ${
            messageType === "success"
              ? "so-sub-msg-success"
              : messageType === "error"
              ? "so-sub-msg-error"
              : "so-sub-msg-info"
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );
}

function Feature({ text }) {
  return (
    <div className="so-sub-feature flex items-center gap-2 text-sm">
      <Check size={18} className="so-sub-feature-icon flex-shrink-0" />
      <span>{text}</span>
    </div>
  );
}

// =========================================================
// SCOPED SWIFTORDER THEME — same tokens as the rest of the POS.
// =========================================================

const SO_SUB_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

  .so-sub {
    --ink: #1c1a17;
    --paper: #fbf9f4;
    --paper-dim: #f2ede1;
    --chili: #e4572e;
    --chili-dark: #c43f1a;
    --herb: #3f6b4f;
    --herb-dark: #2d4f39;
    --stone: #6b6459;
    --stone-light: #a49c8d;
    --line: #ddd4c2;
    --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
    --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    --font-mono: 'JetBrains Mono', 'Courier New', monospace;

    font-family: var(--font-body);
    color: var(--ink);
  }

  .so-sub-loading-screen {
    background: var(--paper-dim);
    background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
    background-size: 24px 24px;
  }

  .so-sub * {
    box-sizing: border-box;
  }

  .so-sub-spinner {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    border: 3px solid var(--line);
    border-bottom-color: var(--chili);
    animation: so-sub-spin 0.8s linear infinite;
  }

  @keyframes so-sub-spin {
    to { transform: rotate(360deg); }
  }

  .so-sub-loading-text {
    color: var(--stone);
  }

  .so-sub-crown-wrap {
    background: #fdece6;
  }

  .so-sub-crown-icon {
    color: var(--chili);
  }

  .so-sub-h1 {
    font-family: var(--font-display);
    font-size: 2.4rem;
    letter-spacing: 0.01em;
    color: var(--ink);
  }

  .so-sub-subtext {
    color: var(--stone);
  }

  .so-sub-panel {
    background: #fffdf8;
    border: 1px solid var(--line);
    border-radius: 16px;
    box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
  }

  .so-sub-label {
    font-size: 0.78rem;
    font-weight: 700;
    font-family: var(--font-mono);
    letter-spacing: 0.03em;
    color: var(--stone);
  }

  .so-sub-plan-name {
    font-family: var(--font-display);
    font-size: 1.7rem;
    letter-spacing: 0.01em;
    color: var(--ink);
  }

  .so-sub-badge-active {
    background: #eaf3ee;
    color: var(--herb-dark);
  }

  .so-sub-badge-trial {
    background: #e9eef5;
    color: #35537d;
  }

  .so-sub-badge-expired {
    background: #fdece6;
    color: var(--chili-dark);
  }

  .so-sub-remaining-neutral {
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--ink);
  }

  .so-sub-remaining-active {
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--herb-dark);
  }

  .so-sub-remaining-expired {
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--chili-dark);
  }

  .so-sub-info-trial {
    background: #eef2f8;
    border: 1px solid #c7d4e6;
    border-radius: 12px;
  }

  .so-sub-info-icon-trial {
    color: #35537d;
  }

  .so-sub-info-title-trial {
    font-weight: 700;
    color: #2a415f;
  }

  .so-sub-info-text-trial {
    color: #35537d;
  }

  .so-sub-info-sub-trial {
    color: #4c6a92;
  }

  .so-sub-info-active {
    background: #eaf3ee;
    border: 1px solid #bfe0cd;
    border-radius: 12px;
  }

  .so-sub-info-title-active {
    font-weight: 700;
    color: var(--herb-dark);
  }

  .so-sub-info-text-active {
    color: var(--herb-dark);
  }

  .so-sub-info-sub-active {
    color: var(--herb);
  }

  .so-sub-info-expired {
    background: #fdece6;
    border: 1px solid #f3c3b0;
    border-radius: 12px;
  }

  .so-sub-info-icon-expired {
    color: var(--chili-dark);
  }

  .so-sub-info-title-expired {
    font-weight: 700;
    color: var(--chili-dark);
  }

  .so-sub-info-text-expired {
    color: var(--chili-dark);
  }

  .so-sub-plan-card {
    background: #fffdf8;
    border: 2px solid var(--chili);
    border-radius: 16px;
    box-shadow: 0 20px 40px -24px rgba(228, 87, 46, 0.4);
  }

  .so-sub-plan-top {
    background: var(--chili);
    color: var(--paper);
  }

  .so-sub-plan-kicker {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    letter-spacing: 0.06em;
    color: #fbd9cb;
  }

  .so-sub-plan-title {
    font-family: var(--font-display);
    font-size: 1.8rem;
    letter-spacing: 0.01em;
  }

  .so-sub-price {
    font-family: var(--font-display);
    font-size: 2.6rem;
    letter-spacing: 0.01em;
  }

  .so-sub-price-period {
    color: #fbd9cb;
  }

  .so-sub-features-title {
    font-weight: 700;
    color: var(--ink);
  }

  .so-sub-feature {
    color: var(--ink);
  }

  .so-sub-feature-icon {
    color: var(--herb);
  }

  .so-sub-subscribe-btn {
    border-radius: 10px;
    color: var(--paper);
    font-weight: 700;
    transition: background 0.15s ease;
  }

  .so-sub-btn-default {
    background: var(--chili);
    box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
    cursor: pointer;
  }

  .so-sub-btn-default:hover {
    background: var(--chili-dark);
  }

  .so-sub-btn-waiting {
    background: var(--stone-light);
    cursor: wait;
  }

  .so-sub-btn-active {
    background: var(--herb);
    cursor: not-allowed;
  }

  .so-sub-secure-note {
    color: var(--stone-light);
  }

  .so-sub-msg-success {
    background: #eaf3ee;
    border-color: #bfe0cd;
    color: var(--herb-dark);
  }

  .so-sub-msg-error {
    background: #fdece6;
    border-color: #f3c3b0;
    color: var(--chili-dark);
  }

  .so-sub-msg-info {
    background: #eef2f8;
    border-color: #c7d4e6;
    color: #35537d;
  }
`;


